import { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOSSafari() {
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

function wasRecentlyDismissed() {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  const days = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
  return days < DISMISS_DAYS;
}

export default function InstallPWAPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    if (isIOSSafari()) {
      // iOS Safari has no beforeinstallprompt — show manual instructions after a delay
      const t = setTimeout(() => { setIosMode(true); setShow(true); }, 8000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShow(false);
  }

  async function install() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShow(false);
    setDeferredPrompt(null);
  }

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[9998] bg-dark-800 border border-accent-primary/30 rounded-2xl shadow-2xl p-4 animate-fade-in">
      <button onClick={dismiss} className="absolute top-3 right-3 text-gray-500 hover:text-white">
        <X className="w-4 h-4" />
      </button>
      {iosMode ? (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Download className="w-5 h-5 text-accent-primary" />
            <h3 className="text-sm font-bold text-white">Install haythix</h3>
          </div>
          <p className="text-xs text-gray-400 mb-3">Add to your Home Screen for a full-screen, app-like experience with push-style alerts.</p>
          <div className="bg-dark-700/50 rounded-lg p-3 text-xs text-gray-300 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-accent-primary font-bold">1.</span>
              <span>Tap the</span>
              <Share className="w-3.5 h-3.5 inline-block text-blue-400" />
              <span>Share button</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-accent-primary font-bold">2.</span>
              <span>Scroll and tap</span>
              <Plus className="w-3.5 h-3.5 inline-block" />
              <span className="font-semibold">Add to Home Screen</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-accent-primary font-bold">3.</span>
              <span>Tap <span className="font-semibold">Add</span></span>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Download className="w-5 h-5 text-accent-primary" />
            <h3 className="text-sm font-bold text-white">Install haythix</h3>
          </div>
          <p className="text-xs text-gray-400 mb-3">Get the full-screen app experience with offline support and faster loads.</p>
          <button onClick={install} className="w-full bg-accent-primary text-black font-semibold text-sm py-2 rounded-lg hover:bg-accent-primary/90 transition-colors">
            Install App
          </button>
        </div>
      )}
    </div>
  );
}
